import mlApi from '@/api/ml';

const state = () => ({
  source: {
    mode: 'visual',
    sql: '',
    datasource: '',
    schema: '',
    table: '',
  },
  datasources: [],
  schemas: [],
  tables: [],
  datasourcesLoading: false,
  schemasLoading: false,
  tablesLoading: false,
  columnsLoading: false,
  rawColumns: [],
  columnIncluded: {},
  columnDataTypes: {},
  tableHeaders: [],
  tableItems: [],
  totalCount: 0,
  previewLoading: false,
});

const mutations = {
  SET_SOURCE(state, patch) {
    Object.assign(state.source, patch);
  },
  SET_DATASOURCES(state, list) {
    state.datasources = list;
  },
  SET_SCHEMAS(state, list) {
    state.schemas = list;
  },
  SET_TABLES(state, list) {
    state.tables = list;
  },
  SET_RAW_COLUMNS(state, list) {
    state.rawColumns = list;
  },
  SET_COLUMN_INCLUDED(state, map) {
    state.columnIncluded = map;
  },
  SET_COLUMN_DATA_TYPES(state, map) {
    state.columnDataTypes = map;
  },
  SET_TABLE_HEADERS(state, list) {
    state.tableHeaders = list;
  },
  SET_TABLE_ITEMS(state, list) {
    state.tableItems = list;
  },
  SET_TOTAL_COUNT(state, count) {
    state.totalCount = count;
  },
  SET_LOADING(state, { key, val }) {
    state[key] = val;
  },
  RESET_COLUMNS(state) {
    state.rawColumns = [];
    state.columnIncluded = {};
    state.columnDataTypes = {};
    state.tableHeaders = [];
    state.tableItems = [];
    state.totalCount = 0;
  },
  RESET_TABLES(state) {
    state.tables = [];
    state.source.table = '';
  },
  RESET_SCHEMAS(state) {
    state.schemas = [];
    state.source.schema = '';
  },
};

const actions = {
  loadDatasources({ commit }) {
    commit('SET_LOADING', { key: 'datasourcesLoading', val: true });
    mlApi.getDataSources()
      .then(resp => {
        if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
          commit('SET_DATASOURCES', resp.data.result);
        }
      })
      .catch(error => {
        console.error('ml/training.loadDatasources', error);
      })
      .finally(() => {
        commit('SET_LOADING', { key: 'datasourcesLoading', val: false });
      });
  },

  onDatasourceChange({ commit, dispatch }, name) {
    commit('SET_SOURCE', { datasource: name, schema: '', table: '' });
    commit('RESET_SCHEMAS');
    commit('RESET_TABLES');
    commit('RESET_COLUMNS');
    dispatch('loadSchemas', name);
  },

  loadSchemas({ commit, state }, datasourceName) {
    const name = datasourceName || state.source.datasource;
    const dataSource = state.datasources.find(item => item.name === name);
    if (!dataSource) return;
    commit('SET_LOADING', { key: 'schemasLoading', val: true });
    mlApi.getSchemas(dataSource)
      .then(resp => {
        if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
          commit('SET_SCHEMAS', resp.data.result);
        }
      })
      .catch(error => {
        console.error('ml/training.loadSchemas', error);
      })
      .finally(() => {
        commit('SET_LOADING', { key: 'schemasLoading', val: false });
      });
  },

  onSchemaChange({ commit, dispatch, state }, schema) {
    commit('SET_SOURCE', { schema, table: '' });
    commit('RESET_TABLES');
    commit('RESET_COLUMNS');
    dispatch('loadTables', { datasource: state.source.datasource, schema });
  },

  loadTables({ commit, state }, { datasource, schema }) {
    const dataSource = state.datasources.find(item => item.name === datasource);
    if (!dataSource || !schema) return;
    commit('SET_LOADING', { key: 'tablesLoading', val: true });
    mlApi.getTables(dataSource, schema)
      .then(resp => {
        if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
          commit('SET_TABLES', resp.data.result);
        }
      })
      .catch(error => {
        console.error('ml/training.loadTables', error);
      })
      .finally(() => {
        commit('SET_LOADING', { key: 'tablesLoading', val: false });
      });
  },

  onTableChange({ commit, dispatch, state }, table) {
    commit('SET_SOURCE', { table });
    commit('RESET_COLUMNS');
    dispatch('loadColumns', { datasource: state.source.datasource, schema: state.source.schema, table });
  },

  loadColumns({ commit, state }, { datasource, schema, table }) {
    const dataSource = state.datasources.find(item => item.name === datasource);
    if (!dataSource || !schema || !table) return;
    commit('SET_LOADING', { key: 'columnsLoading', val: true });
    mlApi.getColumns(dataSource, schema, table)
      .then(resp => {
        if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
          const cols = resp.data.result;
          const included = {};
          const types = {};
          cols.forEach(col => {
            included[col.columnName] = true;
            types[col.columnName] = mapDataType(col.dataType);
          });
          commit('SET_RAW_COLUMNS', cols);
          commit('SET_COLUMN_INCLUDED', included);
          commit('SET_COLUMN_DATA_TYPES', types);
        }
      })
      .catch(error => {
        console.error('ml/training.loadColumns', error);
      })
      .finally(() => {
        commit('SET_LOADING', { key: 'columnsLoading', val: false });
      });
  },

  loadPreview({ commit, state }) {
    const sql = state.source.sql;
    if (!sql) return;
    commit('SET_LOADING', { key: 'previewLoading', val: true });
    commit('SET_TABLE_HEADERS', []);
    commit('SET_TABLE_ITEMS', []);
    mlApi.executeDatasetSql(sql)
      .then(resp => {
        if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
          const result = resp.data.result;
          commit('SET_TOTAL_COUNT', result.totalCount || 0);
          commit('SET_TABLE_HEADERS', (result.headers || []).map(h => ({
            text: h.name,
            value: h.name,
            dataType: h.dataType,
          })));
          commit('SET_TABLE_ITEMS', result.values || []);
        }
      })
      .catch(error => {
        console.error('ml/training.loadPreview', error);
      })
      .finally(() => {
        commit('SET_LOADING', { key: 'previewLoading', val: false });
      });
  },

  setSql({ commit }, sql) {
    commit('SET_SOURCE', { sql });
  },

  setMode({ commit }, mode) {
    commit('SET_SOURCE', { mode });
  },

  toggleColumn({ commit, state }, columnName) {
    const updated = Object.assign({}, state.columnIncluded, {
      [columnName]: !state.columnIncluded[columnName],
    });
    commit('SET_COLUMN_INCLUDED', updated);
  },

  setColumnType({ commit, state }, { columnName, type }) {
    const updated = Object.assign({}, state.columnDataTypes, { [columnName]: type });
    commit('SET_COLUMN_DATA_TYPES', updated);
  },

  selectAllColumns({ commit, state }) {
    const updated = {};
    state.rawColumns.forEach(col => { updated[col.columnName] = true; });
    commit('SET_COLUMN_INCLUDED', updated);
  },

  deselectAllColumns({ commit, state }) {
    const updated = {};
    state.rawColumns.forEach(col => { updated[col.columnName] = false; });
    commit('SET_COLUMN_INCLUDED', updated);
  },

  clearAll({ commit }) {
    commit('RESET_COLUMNS');
    commit('SET_SOURCE', { table: '' });
  },
};

function mapDataType(t) {
  if (!t) return 'Numeric';
  const lower = t.toLowerCase();
  if (lower.includes('string') || lower.includes('varchar') || lower.includes('char') || lower.includes('text') || lower.includes('clob')) return 'Categoric';
  if (lower.includes('date') || lower.includes('time')) return 'Proper';
  if (lower === 'int16' || lower === 'boolean' || lower === 'bit') return 'Binary';
  return 'Numeric';
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
